import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Trophy, Crown, Medal, AlertTriangle } from 'lucide-react';
import CastleVisual from './CastleVisual';
import './Castle.css';

const TopRow = ({ rank, r }) => {
  const Rank = rank === 0 ? Crown : rank < 3 ? Medal : null;
  return (
    <div
      className="flex items-center gap-3 bg-gradient-to-l from-white via-white to-amber-50/40 rounded-xl p-3 border border-amber-100/80 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
      data-testid={`honor-row-${r.reader_number}`}
    >
      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-transform duration-300 ${rank === 0 ? 'bg-amber-100 text-amber-700' : rank === 1 ? 'bg-gray-100 text-gray-600' : rank === 2 ? 'bg-orange-100 text-orange-600' : 'bg-muted/40 text-muted-foreground text-xs font-bold'}`}>
        {Rank ? <Rank className="w-4 h-4" /> : <span>{rank + 1}</span>}
      </div>
      <CastleVisual
        index={r.castle_index ?? 0}
        fillPercent={r.fill_percent ?? 0}
        cracked={!!r.cracked}
        color={r.level_color}
        size="sm"
      />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-accent truncate text-sm">{r.name}</p>
        <p className="text-[10px] text-muted-foreground flex items-center gap-1.5 flex-wrap">
          <span>{r.level_name}</span>
          <span className="text-muted-foreground/60">•</span>
          <span style={{ color: r.level_color }} className="font-bold">{r.fill_percent}%</span>
          {typeof r.total_active_days === 'number' && r.total_active_days > 0 && (
            <>
              <span className="text-muted-foreground/60">•</span>
              <span className="text-muted-foreground/90" title="إجمالي أيام النشاط (كاسر التعادل)">
                {r.total_active_days} يوم نشاط
              </span>
            </>
          )}
          {r.cracked && (
            <span className="inline-flex items-center gap-0.5 text-red-500" title="القلعة متصدعة">
              <AlertTriangle className="w-3 h-3" />
              <span>تصدّع</span>
            </span>
          )}
        </p>
      </div>
    </div>
  );
};

const HonorBoardCard = () => {
  const { user } = useAuth();
  const [board, setBoard] = useState({ males: [], females: [] });
  const [loading, setLoading] = useState(true);

  const isFemale = user?.gender === 'female';
  const list = isFemale ? board.females : board.males;
  const heading = isFemale ? 'القارئات الأكثر مواصلة' : 'القراء الأكثر مواصلة';
  const subtitle = 'مرتبون حسب القلعة، ثم النسبة، ثم إجمالي أيام النشاط — تحديث تلقائي';

  useEffect(() => {
    api.get('/streak/honor-board?limit=0')
      .then((r) => setBoard(r.data || { males: [], females: [] }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  return (
    <div className="ds-card" data-testid="honor-board-public">
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-start gap-3">
          <Trophy className="w-6 h-6 text-amber-500 mt-1 shrink-0" />
          <div>
            <p className="ds-kicker">اللوحة الشرفية</p>
            <h3 className="ds-title">{heading}</h3>
            <p className="ds-subtitle">{subtitle}</p>
          </div>
        </div>
      </div>

      {list.length === 0 ? (
        <p className="text-muted-foreground text-center py-8 text-sm">لم تبدأ القلاع بالتشييد بعد — كن أنت الأول!</p>
      ) : (
        <div
          className="ds-row-stagger grid grid-cols-1 md:grid-cols-2 gap-2.5 max-h-[440px] overflow-y-auto pl-1 pr-0.5"
          data-testid="honor-board-list"
        >
          {list.map((r, i) => <TopRow key={r.reader_number} rank={i} r={r} />)}
        </div>
      )}
      {list.length > 10 && (
        <p className="text-[11px] text-muted-foreground text-center mt-3" data-testid="honor-board-scroll-hint">
          {isFemale ? 'مرّري للأسفل لرؤية بقية القارئات وموقعك بينهن ↓' : 'مرّر للأسفل لرؤية بقية القراء وموقعك بينهم ↓'}
        </p>
      )}
    </div>
  );
};

export default HonorBoardCard;
